import { useState, useEffect, useContext, useRef, useMemo } from 'react';

import { PracticeReseveContext } from './PracticeReseveContext';
import PracticeReserveBeforeLesson from './beforeLesson/PracticeReserveBeforeLesson';
import PracticeReserveForm from './form/PracticeReserveForm';
import Loading from '../../common/loading/Loading';
import PracticeReserveHowto from '../howto/PracticeReserveHowto';
import { jaRandomInstructor } from './PracticeReserveJaRandomInstructor';

import {
  requestApi,
  formatDate, formatTime,
} from '../../../util/utility';

import { PracticeReserveSituationDisplaySituations } from '../../../models/practice/reserve/situation/PracticeReserveSituation';
import { PracticeReserveInstructorModel } from '../../../models/practice/reserve/instructor/PracticeReserveInstructor';
import { PRACTICE_RESERVE_MODE, PracticeReserveMode } from '../../../models/practice/reserve/mode/PracticeReserveMode';
import { PracticeReserveCalendarInsts, PracticeReserveCalendarJa } from '../../../models/practice/reserve/calendar/PracticeReserveCalendar';


function PracticeReserve() {
  // パラメータ(初回データ)
  const params = new URLSearchParams(window.location.search);
  const dateNow = params.get('date_now') || '';
  const dfInstId = params.get('inst_id') || '';
  // モード（講師から選ぶ／日時から選ぶ）を切り替えられるのは sub_course=all のときだけ。それ以外は講師から選ぶのみ
  const canSelectReserveMode = params.get('sub_course') === 'all';
  // 予約エラーで画面が読み込み直されたとき、日時から選ぶで送っていれば日時から選ぶで開く
  //   講師の指定(inst_id)があるときは講師から選ぶで開く
  const dfReserveMode: PracticeReserveMode = (canSelectReserveMode && !dfInstId && params.get('reserve_mode') === PRACTICE_RESERVE_MODE.DATETIME)
    ? PRACTICE_RESERVE_MODE.DATETIME
    : PRACTICE_RESERVE_MODE.INST;
  const lessonTermMinute = 15;
  const [user, setUser] = useState<any>(null);
  const [config, setConfig] = useState(null);
  const [displaySituations, setDisplaySituations] = useState<PracticeReserveSituationDisplaySituations>([]);
  const [vars, setVars] = useState(null);
  const formRef = useRef<HTMLFormElement>(null);
  // 入力値
  const [reserveMode, setReserveMode] = useState<PracticeReserveMode>(dfReserveMode);
  const [instId, setInstId] = useState<string|null>(dfInstId);
  const [lsnMin, setLsnMin] = useState<number>(lessonTermMinute);
  const [isTrial, setIsTrial] = useState<boolean>(true);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string|null>(null);
  const [selectedSituation, setSelectedSituation] = useState<string|null>(null);
  const [requestContent, setRequestContent] = useState<string>('');
  // 出力値
  const [title, setTitle] = useState<string>('');
  const [instructors, setInstructors] = useState<PracticeReserveInstructorModel[]>([]);
  const [instProfile, setInstProfile] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [chkCal, setChkCal] = useState({});
  // 日時から選ぶ用
  const [calendarInsts, setCalendarInsts] = useState<PracticeReserveCalendarInsts>({});
  const [calendarJa, setCalendarJa] = useState<PracticeReserveCalendarJa>({});

  const [isLesson, setIsLesson] = useState<boolean>(true);
  const [trialCount, setTrialCount] = useState<number | null>(null);
  const [isTicketLess, setIsTicketLess] = useState<boolean>(false);
  const [lessTicketCount, setLessTicketCount] = useState<number>(0);

  // キッズプラクティス
  const [kidPracticeInstIds, setKidPracticeInstIds] = useState<[]>([]);

  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);


  // API に渡す講師：日時から選ぶでは講師を選んでも呼び直さない（呼び直すと日時が外れるため）
  const apiInstId = reserveMode === PRACTICE_RESERVE_MODE.DATETIME ? '' : instId;


  // サーバアクセス
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      try {
        // API実行
        const resp = await requestApi({
          inc: '/backend/api/functions/user/practice',
          date_now: dateNow,
          inst_id: apiInstId,
          lesson_minutes: lsnMin,
        });
        if (cancelled) return;

        // 取得データの格納
        setDisplaySituations(resp.displaySituations);
        setConfig(resp.config);
        setTitle(resp.title);
        setVars(resp.vars);
        setUser(resp.user);
        // 先頭に日本語可能な講師(ランダム)を足す
        setInstructors([jaRandomInstructor, ...(resp.instructor || [])]);
        setInstProfile(resp.instProfile);
        setIsLesson(resp.isLesson);
        setCalendar(resp.calendar);
        setChkCal(resp.chkCal);
        setCalendarInsts(resp.calendarInsts || {});
        setCalendarJa(resp.calendarJa || {});
        setIsTicketLess(resp.isTicketLess);
        setLessTicketCount(resp.lessTicketCount);
        setKidPracticeInstIds(resp.kidPracticeInstIds || []);
        // 初期化
        setSelectedScheduleId(null);
      } catch(err) {
        if (err instanceof Error) {
          console.warn(err.message);
        }
      } finally {
        setIsFirstLoad(true);
        setTimeout(() => {
          setIsLoading(false);
        }, 100)
      }
    })();

    // cleanup関数でキャンセルフラグを立てる
    return () => {
      cancelled = true;
    };
  }, [apiInstId, lsnMin])


  // 無料回数
  useEffect(() => {
    if(user) {
      if(user?.register_items?.practice) {
        setTrialCount(user?.register_items?.practice);
      } else {
        setTrialCount(0);
      }
    }
  }, [user])


  // モード変更：講師と日時は選び直してもらう（レッスン時間・内容・コメントは残す）
  //   講師が変わると API を呼び直し、そこで日時も外れる。講師が '' のままなら呼び直さないので、ここで日時を外す
  const changeReserveMode = (mode: PracticeReserveMode): void => {
    setReserveMode(mode);
    setInstId('');
    setSelectedScheduleId(null);
  }



  //--------------------------------------------------
  // 日時から選ぶ
  //   selectedScheduleId は「どの日時か」を表すためだけに使い、実際に送る枠は submitScheduleId
  //--------------------------------------------------
  const isDatetimeMode = reserveMode === PRACTICE_RESERVE_MODE.DATETIME;

  // 選んだ日時（selectedScheduleId から calendar を逆引き）
  const selectedDatetime = useMemo((): { date: string, time: string } | null => {
    if(!selectedScheduleId) return null;
    for(const date in calendar) {
      const times = calendar[date];
      for(const time in times) {
        if(times[time] == selectedScheduleId) {
          return { date, time };
        }
      }
    }
    return null;
  }, [calendar, selectedScheduleId]);

  // 選んだ日時に空いている講師の枠
  const datetimeInsts = useMemo(() => {
    if(!selectedDatetime) return [];
    return calendarInsts?.[selectedDatetime.date]?.[selectedDatetime.time] || [];
  }, [calendarInsts, selectedDatetime]);
  const datetimeJaScheduleId: string | null = selectedDatetime
    ? calendarJa?.[selectedDatetime.date]?.[selectedDatetime.time] || null
    : null;

  // 講師一覧：日時から選ぶでは、選んだ日時に空いている講師だけ
  const dispInstructors = useMemo(() => {
    if(!isDatetimeMode || !selectedDatetime) return instructors;
    return instructors.filter((inst: PracticeReserveInstructorModel) => {
      if(inst.inst_id === jaRandomInstructor.inst_id) {
        return !!datetimeJaScheduleId;
      }
      return datetimeInsts.some((v) => String(v.inst_id) === String(inst.inst_id));
    });
  }, [isDatetimeMode, selectedDatetime, instructors, datetimeInsts, datetimeJaScheduleId]);

  // 絞る前の講師一覧（日本語可能な講師(ランダム)を除く）：日時から選ぶで30分を選べるかの判定用
  const allInstructors = useMemo(() => {
    return instructors.filter((inst: PracticeReserveInstructorModel) => inst.inst_id !== jaRandomInstructor.inst_id);
  }, [instructors]);

  // 講師プロフィール（フリートピック）：日時から選ぶでは講師一覧の free_topics から作る
  const dispInstProfile = useMemo(() => {
    if(!isDatetimeMode) return instProfile;
    const inst = allInstructors.find((inst: PracticeReserveInstructorModel) => String(inst.inst_id) === String(instId));
    return inst ? { name: inst.name, free_topics: inst.free_topics || [] } : null;
  }, [isDatetimeMode, instProfile, allInstructors, instId]);

  // 実際に送る枠
  //   未選択(外国人講師(ランダム)) … selectedScheduleId（今のランダムと同じ枠）
  //   日本語可能な講師(ランダム)   … calendarJa[日付][時間]
  //   講師ID                      … calendarInsts[日付][時間] のその講師の枠
  const submitScheduleId = useMemo((): string | null => {
    if(!isDatetimeMode || !selectedScheduleId || !instId) return selectedScheduleId;
    if(instId === jaRandomInstructor.inst_id) return datetimeJaScheduleId;
    return datetimeInsts.find((v) => String(v.inst_id) === String(instId))?.sche_id || null;
  }, [isDatetimeMode, selectedScheduleId, instId, datetimeInsts, datetimeJaScheduleId]);

  // 日時を選び直したとき（レッスン時間の変更で日時が外れたときも）、選んでいた講師が空いていなければ外国人講師(ランダム)に戻す
  useEffect(() => {
    if(!isDatetimeMode || !instId) return;
    const isAvailable = dispInstructors.some((inst: PracticeReserveInstructorModel) => String(inst.inst_id) === String(instId));
    if(!selectedDatetime || !isAvailable) {
      setInstId('');
    }
  }, [isDatetimeMode, selectedDatetime, dispInstructors])



  const contextVal = {
    lessonTermMinute: lessonTermMinute,
    user: user,
    config: config,
    vars: vars,
    setVars: setVars,
    title: title,
    isLesson: isLesson,
    isLoading: isLoading,
    formRef: formRef,
    // モード（講師から選ぶ／日時から選ぶ）
    canSelectReserveMode: canSelectReserveMode,
    reserveMode: reserveMode,
    changeReserveMode: changeReserveMode,
    // 講師データ
    instId: instId,
    setInstId: setInstId,
    instructors: dispInstructors,
    allInstructors: allInstructors,
    instProfile: dispInstProfile,
    kidPracticeInstIds: kidPracticeInstIds,
    // 日時選択
    calendar: calendar,
    chkCal: chkCal,
    // 時間選択データ
    trialCount: trialCount,
    lsnMin: lsnMin,
    setLsnMin: setLsnMin,
    isTrial: isTrial,
    setIsTrial: setIsTrial,
    // 日時選択データ
    selectedScheduleId: selectedScheduleId,
    setSelectedScheduleId: setSelectedScheduleId,
    submitScheduleId: submitScheduleId,
    // シチュエーション
    displaySituations: displaySituations,
    selectedSituation: selectedSituation,
    setSelectedSituation: setSelectedSituation,
    // コメント
    requestContent: requestContent,
    setRequestContent: setRequestContent,
    // 送信
    isTicketLess: isTicketLess,
    lessTicketCount: lessTicketCount,
    // 日時フォーマット関数
    formatDate: formatDate,
    formatTime: formatTime,
  }


  return (
    <PracticeReseveContext.Provider value={contextVal}>
      {isFirstLoad ?
        <>
          {/* 見出し + 予約方法ボタン */}
          <div className="hidden-xs" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <h3 id="practice_reservation" className="midashi1 bold" style={{ margin: 0 }}>プラクティスを予約する</h3>
            <PracticeReserveHowto />
          </div>
          <div className="visible-xs">
            <h3 id="practice_reservation" className="midashi1 bold">プラクティスを予約する</h3>
            <div style={{ textAlign: 'center', marginBottom: '15px' }}>
              <PracticeReserveHowto />
            </div>
          </div>

          <div style={{ margin: '0 -15px', padding: '15px', backgroundColor: '#f0f0f0' }}>

            {/* レッスンを開始していない */}
            <PracticeReserveBeforeLesson />

            {/* 予約フォーム */}
            <PracticeReserveForm />

          </div>
        </>
      :
        <Loading />
      }
    </PracticeReseveContext.Provider>
  );
}
  
export default PracticeReserve;